import { useContext, useEffect, useMemo, useRef } from 'react'
import { AuthContext } from './use-auth'
import type {
    WorkflowContext,
    WorkflowSearch,
} from '@/lib/order-workflow-contract'
import type {
    WorkflowStepMetadata,
    WorkflowTracker,
} from '@/lib/workflow-tracking'
import type {
    WorkflowAction,
    WorkflowGroup,
    WorkflowOperation,
    WorkflowOutcome,
    WorkflowType,
} from '@/lib/workflow-contract'
import { getAnalyticsActor, posthog } from '@/lib/posthog'
import { createWorkflowTracker } from '@/lib/workflow-tracking'
import { workflowErrorCode } from '@/lib/payment-collection-tracking'

type AttemptOwner = {
    tracker: WorkflowTracker | null
    identityKey: string
    attemptKey?: string
    mounted: boolean
    context?: WorkflowContext
}

/** The form/dialog/cart owner supplies its active state and stable work key. */
export function useWorkflowTracking(
    type: WorkflowType,
    options: {
        retainAttempts?: boolean
        retainedKeys?: readonly string[]
        autoStart?: boolean
        active?: boolean
        attemptKey?: string
        operation?: WorkflowOperation
    } = {},
) {
    const auth = useContext(AuthContext)
    const analyticsActor = getAnalyticsActor()
    const actorId = auth
        ? (auth.impersonating?.actor.id ?? auth.user?.id)
        : analyticsActor?.actor
    const impersonating = auth
        ? Boolean(auth.impersonating)
        : (analyticsActor?.impersonating ?? false)
    const identityKey = `${actorId ?? ''}:${auth?.user?.id ?? ''}:${impersonating}`
    const {
        active = true,
        autoStart = true,
        attemptKey,
        operation,
        retainAttempts = false,
        retainedKeys,
    } = options
    const session = useMemo(
        () => ({
            owners: new Map<string, AttemptOwner>(),
            mounted: false,
            identityKey,
        }),
        [identityKey, active, retainAttempts],
    )
    const owner = useMemo(() => {
        const key = JSON.stringify([type, operation, attemptKey])
        let value = session.owners.get(key)
        if (!value) {
            value = { tracker: null, identityKey, attemptKey, mounted: false }
            session.owners.set(key, value)
        }
        return value
    }, [session, type, operation, attemptKey, identityKey, retainAttempts])
    const current = useRef(owner)
    current.current = owner
    const currentSession = useRef(session)
    currentSession.current = session
    const controller = useMemo(() => {
        const ensure = (submitted = false) => {
            const retainedSubmit =
                submitted &&
                retainAttempts &&
                session.mounted &&
                currentSession.current === session &&
                [...session.owners.values()].includes(owner)
            if (
                !active ||
                !actorId ||
                (!retainedSubmit &&
                    (!owner.mounted || current.current !== owner))
            )
                return null
            try {
                if (!owner.tracker || owner.tracker.finished) {
                    owner.tracker = createWorkflowTracker(
                        { actorId, impersonating, identityKey },
                        type,
                        {
                            operation,
                            capture: (event, props) => {
                                if (
                                    (session.mounted &&
                                        currentSession.current === session &&
                                        (retainAttempts ||
                                            (owner.mounted &&
                                                current.current === owner))) ||
                                    (current.current.identityKey ===
                                        identityKey &&
                                        event === 'workflow_finished' &&
                                        props.outcome !== 'api_confirmed')
                                )
                                    posthog.capture(event, {
                                        ...props,
                                        // Bank dialogs use the existing balance-event ID
                                        // as their work key, including abandoned attempts.
                                        ...((type === 'balance_assign' ||
                                            type === 'balance_ignore') &&
                                        attemptKey
                                            ? { balance_event_id: attemptKey }
                                            : {}),
                                    })
                            },
                        },
                    )
                    if (owner.context) owner.tracker.setContext(owner.context)
                    owner.tracker.start()
                    owner.tracker.setForeground(
                        current.current === owner &&
                            owner.mounted &&
                            document.visibilityState === 'visible' &&
                            document.hasFocus(),
                    )
                }
                return owner.tracker
            } catch {
                return null
            }
        }
        return {
            get attemptId() {
                return owner.tracker?.attemptId
            },
            start: () => ensure(),
            setContext(metadata: WorkflowContext) {
                owner.context = { ...owner.context, ...metadata }
                owner.tracker?.setContext(metadata)
            },
            search(metadata: WorkflowSearch) {
                owner.tracker?.search(metadata)
            },
            step(action: WorkflowAction, metadata: WorkflowStepMetadata = {}) {
                ensure()?.step(action, metadata)
            },
            interact(group: WorkflowGroup) {
                ensure()?.interact(group)
            },
            finish(
                outcome: WorkflowOutcome,
                properties?: Record<string, unknown>,
            ) {
                owner.tracker?.finish(outcome, undefined, properties)
                if (owner.tracker?.finished) owner.context = undefined
            },
            async run<T>(
                execute: () => Promise<T>,
                recordId?: (result: T) => string | undefined,
                properties?: Record<string, unknown>,
            ): Promise<T> {
                const tracker = ensure(true)
                const settle = tracker?.submit()
                let result: T
                try {
                    result = await execute()
                } catch (error) {
                    settle?.()
                    tracker?.step('api_error', {
                        errorCode: workflowErrorCode(error),
                    })
                    throw error
                }
                settle?.()
                // ID extraction is telemetry too: a bad extractor must not turn success into a failure.
                let id: string | undefined
                try {
                    id = recordId?.(result)
                } catch {
                    /* Preserve the API result. */
                }
                tracker?.finish('api_confirmed', id, properties)
                if (owner.tracker === tracker) owner.context = undefined
                return result
            },
        }
    }, [
        owner,
        active,
        actorId,
        impersonating,
        identityKey,
        type,
        operation,
        session,
        retainAttempts,
    ])
    const previous = useRef({ owner, session })
    useEffect(() => {
        if (
            previous.current.session !== session ||
            (!retainAttempts && previous.current.owner !== owner)
        ) {
            for (const prior of previous.current.session.owners.values()) {
                if (prior !== owner)
                    prior.tracker?.finish(active ? 'navigated_away' : 'closed')
            }
            if (previous.current.session !== session)
                previous.current.session.owners.clear()
        }
        if (!retainAttempts) {
            for (const [key, prior] of session.owners)
                if (prior !== owner) session.owners.delete(key)
        }
        previous.current = { owner, session }
        owner.mounted = true
        session.mounted = true
        if (autoStart) controller.start()
        const update = () =>
            owner.tracker?.setForeground(
                document.visibilityState === 'visible' && document.hasFocus(),
            )
        update()
        const pause = () => owner.tracker?.setForeground(false)
        window.addEventListener('focus', update)
        window.addEventListener('blur', pause)
        window.addEventListener('pagehide', pause)
        document.addEventListener('visibilitychange', update)
        const heartbeat = window.setInterval(
            () => owner.tracker?.observe(),
            1000,
        )
        return () => {
            window.clearInterval(heartbeat)
            window.removeEventListener('focus', update)
            window.removeEventListener('blur', pause)
            window.removeEventListener('pagehide', pause)
            document.removeEventListener('visibilitychange', update)
            pause()
            owner.mounted = false
            session.mounted = false
            // StrictMode remounts this same owner before the microtask runs.
            queueMicrotask(() => {
                if (!session.mounted) {
                    for (const prior of session.owners.values())
                        prior.tracker?.finish('navigated_away')
                    session.owners.clear()
                }
            })
        }
    }, [controller, owner, active, autoStart, session, retainAttempts])
    useEffect(() => {
        if (!retainAttempts || !retainedKeys) return
        for (const [key, prior] of session.owners) {
            if (prior.attemptKey && !retainedKeys.includes(prior.attemptKey)) {
                prior.tracker?.finish('closed')
                session.owners.delete(key)
            }
        }
    }, [session, retainedKeys, retainAttempts])
    return controller
}
